/**
 * AgentStreamingService — SSE consumer for the visual editor streaming endpoint.
 * Uses fetch() + ReadableStream because EventSource only supports GET.
 */
import { config as appConfig } from '@/config';
import { getCurrentAuthTokens } from '@/services/api/auth-session';
import { getValidatedOrganizationContextHeaderValue } from '@/services/api/client';
import type { EditorContext, CanvasAction, AgentVisualResponse, ReasoningStep, ProactiveSuggestion, AccessibilityAuditResult } from '@/services/api/agentApi';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { logger } from '@/utils/logger';
import { SSEEventDataSchema } from '@/schemas/sseEvents';
import { z } from 'zod';

// --- SSE Event types from the agent service ---

export interface SSEStepStart {
  step: string;
  agentName: string;
}

export interface SSEActions {
  step: string;
  actions: CanvasAction[];
  requiresConfirmation?: boolean;
  replaces?: boolean;
}

export interface SSEStepComplete {
  step: string;
  duration_ms: number;
  actionsCount: number;
  /** Agent name that completed this step, if provided by the backend. */
  agentName?: string;
}

export interface SSEMessage {
  text: string;
}

// actions field is NOT included in SSE complete events (Phase 6b) —
// actions are delivered incrementally via per-step 'actions' events during streaming
export interface SSEComplete extends AgentVisualResponse {
  /** Phase 6 optional fields — present when the Phase 6 visual-editor pipeline is active */
  reasoningChain?: ReasoningStep[];
  proactiveSuggestions?: ProactiveSuggestion[];
  accessibilityAudit?: AccessibilityAuditResult;
}

export interface SSEError {
  step?: string;
  message: string;
}

export interface SSEMultiPagePlan {
  pages: Array<{ pageId: string; pageName: string; description?: string }>;
  totalPages: number;
}

export interface SSEPageStart {
  pageIndex: number;
  pageId: string;
  pageName: string;
}

export interface SSEPageComplete {
  pageIndex: number;
  pageId: string;
  pageName: string;
  actionsCount: number;
}

export interface SSELockConflict {
  userId: string;
  userName?: string;
  acquiredAt: string;
  expiresAt: string;
}

export interface SSEMemoryCreated {
  text: string;
  memoryType: 'hypothesis' | 'fact';
  aspect?: string;
}

export interface SSEVerificationResult {
  step: string;
  passed: boolean;
  issues: Array<{
    severity: 'error' | 'warning';
    category: 'structural' | 'sizing' | 'properties' | 'hierarchy' | 'duplicate' | 'interaction';
    componentId?: string;
    message: string;
    suggestion?: string;
  }>;
  summary: string;
  componentCount: number;
  containerCount: number;
}

export interface SSEDiagnostic {
  complexity: string;
  pipeline: string;
  agentType?: string;
  isFollowUp: boolean;
  followUpAgent?: string;
  hasSessionContext: boolean;
  selectedComponentCount: number;
  mqttConnected: boolean;
}

export interface SSESubAgentRetry {
  step: string;
  agent: string;
  attemptNumber: number;
  retryType: 'initial' | 'ratio-or-count' | 'coverage';
  reason?: string;
  potentialFalsePositive?: boolean;
  previousAttempt?: {
    durationMs?: number;
    actionCount?: number;
    interactionRatio?: number;
  };
  gateValues?: Record<string, number | string>;
}

export interface SSESubAgentRetryResult {
  step: string;
  attemptNumber: number;
  retryType: 'initial' | 'ratio-or-count' | 'coverage';
  durationMs: number;
  actionCount: number;
  improved: boolean;
  acceptedAsResult: boolean;
  metrics?: Record<string, number>;
}

export interface SSESubAgentAttemptsSummary {
  step: string;
  agent: string;
  totalAttempts: number;
  totalDurationMs: number;
  wastedDurationMs: number;
  attempts: Array<{
    attemptNumber: number;
    retryType: 'initial' | 'ratio-or-count' | 'coverage';
    startedAt: number;
    durationMs: number;
    actionCount: number;
    accepted: boolean;
  }>;
}

export interface SSEPipelineTiming {
  pipelineType: string;
  wallClockMs: number;
  sumOfDurationsMs: number;
  parallelizationSavingsMs: number;
  componentBatchConcurrency: number;
  durations: {
    dataDiscovery?: number;
    layout?: number;
    components?: number;
    styles?: number;
    dataComponents?: number;
    dataBinding?: number;
  };
  offsets: Record<string, number | undefined>;
}

const CanvasActionArraySchema = z.custom<CanvasAction[]>((value) => Array.isArray(value));
const ReasoningStepArraySchema = z.custom<ReasoningStep[]>((value) => Array.isArray(value));
const ProactiveSuggestionArraySchema = z.custom<ProactiveSuggestion[]>((value) => Array.isArray(value));

const getValidatedStreamingOrganizationContext = async (): Promise<{ slug: string; id?: string } | undefined> => {
  const orgSlug = await getValidatedOrganizationContextHeaderValue();
  if (!orgSlug) {
    return undefined;
  }

  const { organizations, allOrganizations, platformAdminEntitlement } = useOrganizationStore.getState();
  const visibleOrganizations = platformAdminEntitlement
    ? [...organizations, ...allOrganizations]
    : organizations;
  const org = visibleOrganizations.find((organization) => organization.slug === orgSlug);

  return { slug: orgSlug, id: org?._id };
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isAccessibilityAuditResult(value: unknown): value is AccessibilityAuditResult {
  if (!isRecord(value)) {
    return false;
  }

  return typeof value.score === 'number'
    && typeof value.totalChecks === 'number'
    && typeof value.passes === 'number'
    && Array.isArray(value.violations)
    && typeof value.summary === 'string';
}

const AccessibilityAuditResultSchema = z.custom<AccessibilityAuditResult>(isAccessibilityAuditResult);
const SSELockConflictSchema = z.object({
  userId: z.string(),
  userName: z.string().optional(),
  acquiredAt: z.string(),
  expiresAt: z.string(),
});
const SSEThreadSchema = z.object({ threadId: z.string() });
const SSEStepStartSchema = z.object({
  step: z.string(),
  agentName: z.string(),
}).passthrough();
const SSEActionsSchema = z.object({
  step: z.string(),
  actions: CanvasActionArraySchema,
  requiresConfirmation: z.boolean().optional(),
  replaces: z.boolean().optional(),
}).passthrough();
const SSEStepCompleteSchema = z.object({
  step: z.string(),
  duration_ms: z.number(),
  actionsCount: z.number(),
  agentName: z.string().optional(),
}).passthrough();
const SSEMessageSchema = z.object({ text: z.string() }).passthrough();
const SSECompleteSchema = z.object({
  message: z.string(),
  actions: CanvasActionArraySchema.optional(),
  followUpSuggestions: z.array(z.string()).optional(),
  requiresConfirmation: z.boolean().optional(),
  threadId: z.string().optional(),
  memoriesCreated: z.number().optional(),
  memoriesReinforced: z.number().optional(),
  reasoningChain: ReasoningStepArraySchema.optional(),
  proactiveSuggestions: ProactiveSuggestionArraySchema.optional(),
  accessibilityAudit: AccessibilityAuditResultSchema.optional(),
}).passthrough();
const SSEErrorSchema = z.object({
  step: z.string().optional(),
  message: z.string(),
}).passthrough();
const SSEMultiPagePlanSchema = z.object({
  pages: z.array(z.object({
    pageId: z.string(),
    pageName: z.string(),
    description: z.string().optional(),
  }).passthrough()),
  totalPages: z.number(),
}).passthrough();
const SSEPageStartSchema = z.object({
  pageIndex: z.number(),
  pageId: z.string(),
  pageName: z.string(),
}).passthrough();
const SSEPageCompleteSchema = z.object({
  pageIndex: z.number(),
  pageId: z.string(),
  pageName: z.string(),
  actionsCount: z.number(),
}).passthrough();
const SSEMemoryCreatedSchema = z.object({
  text: z.string(),
  memoryType: z.enum(['hypothesis', 'fact']),
  aspect: z.string().optional(),
}).passthrough();
const SSEVerificationResultSchema = z.object({
  step: z.string(),
  passed: z.boolean(),
  issues: z.array(z.object({
    severity: z.enum(['error', 'warning']),
    category: z.enum(['structural', 'sizing', 'properties', 'hierarchy', 'duplicate', 'interaction']),
    componentId: z.string().optional(),
    message: z.string(),
    suggestion: z.string().optional(),
  })),
  summary: z.string(),
  componentCount: z.number(),
  containerCount: z.number(),
}).passthrough();
const SSEDiagnosticSchema = z.object({
  complexity: z.string(),
  pipeline: z.string(),
  agentType: z.string().optional(),
  isFollowUp: z.boolean(),
  followUpAgent: z.string().optional(),
  hasSessionContext: z.boolean(),
  selectedComponentCount: z.number(),
  mqttConnected: z.boolean(),
}).passthrough();
const SSESubAgentRetrySchema = z.object({
  step: z.string(),
  agent: z.string(),
  attemptNumber: z.number(),
  retryType: z.enum(['initial', 'ratio-or-count', 'coverage']),
  reason: z.string().optional(),
  potentialFalsePositive: z.boolean().optional(),
  previousAttempt: z.object({
    durationMs: z.number().optional(),
    actionCount: z.number().optional(),
    interactionRatio: z.number().optional(),
  }).optional(),
  gateValues: z.record(z.string(), z.union([z.number(), z.string()])).optional(),
}).passthrough();
const SSESubAgentRetryResultSchema = z.object({
  step: z.string(),
  attemptNumber: z.number(),
  retryType: z.enum(['initial', 'ratio-or-count', 'coverage']),
  durationMs: z.number(),
  actionCount: z.number(),
  improved: z.boolean(),
  acceptedAsResult: z.boolean(),
  metrics: z.record(z.string(), z.number()).optional(),
}).passthrough();
const SSESubAgentAttemptsSummarySchema = z.object({
  step: z.string(),
  agent: z.string(),
  totalAttempts: z.number(),
  totalDurationMs: z.number(),
  wastedDurationMs: z.number(),
  attempts: z.array(z.object({
    attemptNumber: z.number(),
    retryType: z.enum(['initial', 'ratio-or-count', 'coverage']),
    startedAt: z.number(),
    durationMs: z.number(),
    actionCount: z.number(),
    accepted: z.boolean(),
  })),
}).passthrough();
const SSEPipelineTimingSchema = z.object({
  pipelineType: z.string(),
  wallClockMs: z.number(),
  sumOfDurationsMs: z.number(),
  parallelizationSavingsMs: z.number(),
  componentBatchConcurrency: z.number(),
  durations: z.object({
    dataDiscovery: z.number().optional(),
    layout: z.number().optional(),
    components: z.number().optional(),
    styles: z.number().optional(),
    dataComponents: z.number().optional(),
    dataBinding: z.number().optional(),
  }).passthrough(),
  offsets: z.record(z.string(), z.number().optional()),
}).passthrough();
const SSEComponentManifestSchema = z.object({
  agentType: z.string().default('styles'),
  componentCount: z.number().default(0),
  componentIds: z.array(z.string()).default([]),
  source: z.enum(['selection', 'auto-extracted']).default('auto-extracted'),
}).passthrough();

export interface StreamingCallbacks {
  onThread?: (threadId: string) => void;
  onStepStart?: (data: SSEStepStart) => void;
  onActions?: (data: SSEActions) => void;
  onStepComplete?: (data: SSEStepComplete) => void;
  onMessage?: (data: SSEMessage) => void;
  onComplete?: (data: SSEComplete) => void;
  onError?: (data: SSEError) => void;
  onMultiPagePlan?: (data: SSEMultiPagePlan) => void;
  onPageStart?: (data: SSEPageStart) => void;
  onPageComplete?: (data: SSEPageComplete) => void;
  onLockConflict?: (data: SSELockConflict) => void;
  onMemoryCreated?: (data: SSEMemoryCreated) => void;
  onVerificationResult?: (data: SSEVerificationResult) => void;
  onDiagnostic?: (data: SSEDiagnostic) => void;
  onSubAgentRetry?: (data: SSESubAgentRetry) => void;
  onSubAgentRetryResult?: (data: SSESubAgentRetryResult) => void;
  onSubAgentAttemptsSummary?: (data: SSESubAgentAttemptsSummary) => void;
  onPipelineTiming?: (data: SSEPipelineTiming) => void;
}

export class AgentStreamingService {
  private abortController: AbortController | null = null;

  /**
   * Start a streaming request to the visual editor agent.
   */
  async stream(
    message: string,
    editorContext: EditorContext,
    callbacks: StreamingCallbacks,
    threadId?: string,
    sessionContext?: string,
    classificationMetadata?: {
      isFollowUp: boolean;
      complexity: string;
      mentionedBreakpoints: string[];
      followUpAgent?: string;
      domain?: string;
    },
    extraBody?: Record<string, unknown>,
  ): Promise<void> {
    // Abort any in-flight stream
    this.abort();

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    const tokens = getCurrentAuthTokens();
    const orgContext = await getValidatedStreamingOrganizationContext();

    const url = `${appConfig.api.agentServiceUrl}/api/v1/visual-editor/stream`;

    const body: Record<string, unknown> = { message, editorContext, ...extraBody };
    if (threadId) body.thread_id = threadId;
    if (sessionContext) body.sessionContext = sessionContext;
    if (classificationMetadata) body.classificationMetadata = classificationMetadata;
    if (classificationMetadata?.domain) {
      body.domain = classificationMetadata.domain;
    }
    // Include projectId for page-level locking
    if (editorContext.projectId) body.projectId = editorContext.projectId;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(tokens?.accessToken ? { Authorization: `Bearer ${tokens.accessToken}` } : {}),
          ...(orgContext?.slug ? { 'X-Organization-Context': orgContext.slug } : {}),
          ...(orgContext?.id ? { 'X-Organization-Id': orgContext.id } : {}),
        },
        body: JSON.stringify(body),
        signal,
      });

      if (!response.ok) {
        const text = await response.text();
        let errorMessage: string;
        try {
          const json: unknown = JSON.parse(text);
          const obj = isRecord(json) ? json : {};
          // Handle lock conflict (409)
          if (response.status === 409 && 'lockInfo' in obj && obj.lockInfo) {
            const lockInfoResult = SSELockConflictSchema.safeParse(obj.lockInfo);
            if (lockInfoResult.success) {
              callbacks.onLockConflict?.(lockInfoResult.data);
            }
            return;
          }
          errorMessage = typeof obj.error === 'string' ? obj.error
            : typeof obj.message === 'string' ? obj.message
            : text;
        } catch {
          errorMessage = text || `HTTP ${response.status}`;
        }
        callbacks.onError?.({ message: errorMessage });
        return;
      }

      if (!response.body) {
        callbacks.onError?.({ message: 'No response body (streaming not supported)' });
        return;
      }

      await this.parseSSEStream(response.body, callbacks, signal);
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        // User-initiated abort — not an error
        return;
      }
      callbacks.onError?.({ message: error instanceof Error ? error.message : String(error) });
    } finally {
      this.abortController = null;
    }
  }

  /**
   * Abort an in-flight streaming request.
   */
  abort(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Whether a stream is currently in progress.
   */
  get isStreaming(): boolean {
    return this.abortController !== null;
  }

  /**
   * Parse an SSE stream from a ReadableStream<Uint8Array>.
   */
  private async parseSSEStream(
    body: ReadableStream<Uint8Array>,
    callbacks: StreamingCallbacks,
    signal: AbortSignal,
  ): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = '';
    let currentData = '';

    try {
      while (!signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            currentData += line.slice(6);
          } else if (line.startsWith(':')) {
            // Comment/heartbeat — ignore
          } else if (line === '') {
            // Empty line = dispatch event
            if (currentEvent && currentData) {
              this.dispatchEvent(currentEvent, currentData, callbacks);
            }
            currentEvent = '';
            currentData = '';
          }
        }
      }

      // Process any remaining data
      if (currentEvent && currentData) {
        this.dispatchEvent(currentEvent, currentData, callbacks);
      }
    } catch (error) {
      // Stream closed by server — expected after 'complete' event.
      // AbortError = user-initiated abort, TypeError = network/stream close.
      const name = error instanceof Error ? error.name : '';
      if (name !== 'AbortError' && name !== 'TypeError') {
        throw error;
      }
      // Process any buffered event before swallowing the error
      if (currentEvent && currentData) {
        this.dispatchEvent(currentEvent, currentData, callbacks);
      }
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Dispatch a parsed SSE event to the appropriate callback.
   */
  private dispatchEvent(
    event: string,
    dataStr: string,
    callbacks: StreamingCallbacks,
  ): void {
    try {
      const raw: unknown = JSON.parse(dataStr);
      const baseCheck = SSEEventDataSchema.safeParse(raw);
      if (!baseCheck.success) {
        logger.warn('[AgentStreamingService] SSE event schema validation failed:', baseCheck.error.flatten());
        return;
      }
      const data = baseCheck.data;

      switch (event) {
        case 'thread': {
          const result = SSEThreadSchema.safeParse(data);
          if (result.success) {
            callbacks.onThread?.(result.data.threadId);
          }
          break;
        }
        case 'step_start': {
          const result = SSEStepStartSchema.safeParse(data);
          if (result.success) {
            callbacks.onStepStart?.(result.data);
          }
          break;
        }
        case 'actions': {
          const result = SSEActionsSchema.safeParse(data);
          if (result.success) {
            callbacks.onActions?.(result.data);
          }
          break;
        }
        case 'step_complete': {
          const result = SSEStepCompleteSchema.safeParse(data);
          if (result.success) {
            callbacks.onStepComplete?.(result.data);
          }
          break;
        }
        case 'step_skip':
          // Styles agent skipped (sufficient coverage from prior agents) — treated as instant completion
          callbacks.onStepComplete?.({
            step: typeof data.step === 'string' ? data.step : '',
            duration_ms: 0,
            actionsCount: 0,
          });
          break;
        case 'message': {
          const result = SSEMessageSchema.safeParse(data);
          if (result.success) {
            callbacks.onMessage?.(result.data);
          }
          break;
        }
        case 'complete': {
          const result = SSECompleteSchema.safeParse(data);
          if (!result.success) {
            logger.warn('[AgentStreamingService] SSE complete event validation failed:', result.error.flatten());
            break;
          }
          const completeData = result.data;
          if (Array.isArray(completeData.reasoningChain)) {
            logger.info(`[Phase6:Streaming] Reasoning chain received`, { stepCount: completeData.reasoningChain.length });
          }
          if (Array.isArray(completeData.proactiveSuggestions)) {
            logger.info(`[Phase6:Streaming] Proactive suggestions received`, { count: completeData.proactiveSuggestions.length });
          }
          if (completeData.accessibilityAudit !== undefined) {
            logger.info(`[Phase6:Streaming] Accessibility audit received`, { score: completeData.accessibilityAudit.score });
          }
          callbacks.onComplete?.(completeData);
          break;
        }
        case 'error': {
          const result = SSEErrorSchema.safeParse(data);
          if (result.success) {
            callbacks.onError?.(result.data);
          }
          break;
        }
        case 'multi_page_plan': {
          const result = SSEMultiPagePlanSchema.safeParse(data);
          if (result.success) {
            callbacks.onMultiPagePlan?.(result.data);
          }
          break;
        }
        case 'page_start': {
          const result = SSEPageStartSchema.safeParse(data);
          if (result.success) {
            callbacks.onPageStart?.(result.data);
          }
          break;
        }
        case 'page_complete': {
          const result = SSEPageCompleteSchema.safeParse(data);
          if (result.success) {
            callbacks.onPageComplete?.(result.data);
          }
          break;
        }
        case 'memory_created': {
          const result = SSEMemoryCreatedSchema.safeParse(data);
          if (result.success) {
            callbacks.onMemoryCreated?.(result.data);
          }
          break;
        }
        case 'verification_result': {
          const result = SSEVerificationResultSchema.safeParse(data);
          if (result.success) {
            callbacks.onVerificationResult?.(result.data);
          }
          break;
        }
        case 'diagnostic': {
          const result = SSEDiagnosticSchema.safeParse(data);
          if (result.success) {
            callbacks.onDiagnostic?.(result.data);
          }
          break;
        }
        case 'sub_agent_retry': {
          const result = SSESubAgentRetrySchema.safeParse(data);
          if (result.success) {
            callbacks.onSubAgentRetry?.(result.data);
          }
          break;
        }
        case 'sub_agent_retry_result': {
          const result = SSESubAgentRetryResultSchema.safeParse(data);
          if (result.success) {
            callbacks.onSubAgentRetryResult?.(result.data);
          }
          break;
        }
        case 'sub_agent_attempts_summary': {
          const result = SSESubAgentAttemptsSummarySchema.safeParse(data);
          if (result.success) {
            callbacks.onSubAgentAttemptsSummary?.(result.data);
          }
          break;
        }
        case 'pipeline_timing': {
          const result = SSEPipelineTimingSchema.safeParse(data);
          if (result.success) {
            callbacks.onPipelineTiming?.(result.data);
          }
          break;
        }
        case 'component_manifest': {
          const result = SSEComponentManifestSchema.safeParse(data);
          if (!result.success) break;

          void import('@/stores/ai/debug-store').then(({ useDebugStore }) => {
            const debugStore = useDebugStore.getState();
            if (debugStore.isEnabled) {
              debugStore.pushComponentManifestEvent(result.data);
            }
          }).catch(() => { /* debug store not available */ });
          break;
        }
        default:
          console.warn('AgentStreamingService: unknown SSE event', event);
      }
    } catch (err) {
      console.warn('AgentStreamingService: failed to parse SSE data', event, err);
    }
  }
}

/** Singleton instance */
export const agentStreamingService = new AgentStreamingService();
